import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next"; // 👈 Importación clave
import {
	Utensils,
	Scissors,
	Sparkles,
	Dumbbell,
	ShoppingBag,
	Calendar,
	X,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

import restaurante from "../assets/restaurante.png";
import barberia from "../assets/barberia.png";
import yoga from "../assets/yoga.png";
import belleza from "../assets/salon-belleza.png";
import gym from "../assets/gym.png";
import tienda from "../assets/tienda.png";

export default function SectorSelector() {
	const [showModal, setShowModal] = useState(false);
	const { t } = useTranslation();

	const sectors = [
		{
			title: t("sectors.restaurants"),
			icon: <Utensils className="w-5 h-5 text-primary" />,
			mockup: restaurante,
			link: "/restaurantes",
		},
		{
			title: t("sectors.beauty"),
			icon: <Sparkles className="w-5 h-5 text-primary" />,
			mockup: belleza,
			link: "/salones",
		},
		{
			title: t("sectors.barbers"),
			icon: <Scissors className="w-5 h-5 text-primary" />,
			mockup: barberia,
			link: "/barberias",
		},
		{
			title: t("sectors.trainers"),
			icon: <Dumbbell className="w-5 h-5 text-primary" />,
			mockup: gym,
			link: "/entrenadores",
		},
		{
			title: t("sectors.classes"),
			icon: <Calendar className="w-5 h-5 text-primary" />,
			mockup: yoga,
			contactOnly: true,
		},
		{
			title: t("sectors.store"),
			icon: <ShoppingBag className="w-5 h-5 text-primary" />,
			mockup: tienda,
			link: "/tiendas",
		},
	];

	const handleClick = (s) => {
		if (s.contactOnly) {
			setShowModal(true);
		} else if (s.link) {
			window.location.href = s.link;
		}
	};

	return (
		<section id="sectores" className="py-20 px-6 bg-gray-50">
			<motion.h2
				className="text-4xl md:text-5xl font-extrabold text-center text-gray-900 mb-12"
				initial={{ opacity: 0, y: -20 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8, ease: "easeOut" }}
				viewport={{ once: true, amount: 0.3 }}
			>
				{t("sectors.title")}{" "}
				<span className="text-primary">{t("sectors.highlight")}</span>
			</motion.h2>

			{/* Grid */}
			<div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
				{sectors.map((s, i) => (
					<motion.div
						key={s.title}
						onClick={() => handleClick(s)}
						whileHover={{ scale: 1.03 }}
						whileTap={{ scale: 0.96 }}
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ delay: i * 0.1, duration: 0.6, ease: "easeOut" }}
						viewport={{ once: true, amount: 0.2 }}
						className="cursor-pointer"
						style={{
							willChange: "transform, opacity",
							transform: "translateZ(0)",
							backfaceVisibility: "hidden",
							WebkitFontSmoothing: "antialiased",
						}}
					>
						<div className="h-full bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition flex flex-col">
							{/* Imagen */}
							<div className="relative w-full aspect-[4/3] bg-gray-100">
								<img
									src={s.mockup}
									alt={s.title}
									className="absolute inset-0 w-full h-full object-cover"
								/>
								<div className="absolute top-3 right-3 flex md:hidden items-center justify-center w-9 h-9 rounded-full bg-white shadow-md">
									{s.icon}
								</div>
							</div>

							{/* Texto */}
							<div className="p-4 flex items-center justify-center md:justify-start gap-2">
								<div className="hidden md:flex items-center justify-center w-9 h-9 rounded-full bg-primary/10">
									{s.icon}
								</div>
								<h3 className="text-base font-semibold text-gray-900 text-center md:text-left">
									{s.title}
								</h3>
							</div>
						</div>
					</motion.div>
				))}
			</div>

			{/* Modal */}
			<AnimatePresence>
				{showModal && (
					<motion.div
						className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						<motion.div
							className="bg-white rounded-2xl shadow-xl max-w-md w-full relative text-center"
							initial={{ scale: 0.9, opacity: 0, y: 50 }}
							animate={{ scale: 1, opacity: 1, y: 0 }}
							exit={{ scale: 0.9, opacity: 0, y: 50 }}
							transition={{ duration: 0.3 }}
						>
							<div className="bg-primary/10 p-6 pb-4 relative">
								<h3 className="text-xl font-bold text-gray-900">
									{t("modal.title")}
								</h3>
								<button
									onClick={() => setShowModal(false)}
									className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
								>
									<X className="w-6 h-6" />
								</button>
							</div>

							{/* Body */}
							<div className="p-6 text-center">
								<p className="text-gray-600 leading-relaxed mb-6">
									{t("modal.text")}
								</p>

								<a
									href="https://wa.me/34608916357?text=Hola! Quiero información sobre apps de Clases & Eventos"
									target="_blank"
									rel="noopener noreferrer"
									className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-4 rounded-xl font-semibold shadow hover:shadow-lg transition text-md"
								>
									<FaWhatsapp size={24} />
									{t("modal.button")}
								</a>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</section>
	);
}
